
class CacheManager {
  private cache = new Map<string, { data: unknown; expiry: number }>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get item from cache
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data as T;
  }

  /**
   * Set item in cache
   */
  set(key: string, data: unknown, ttl: number = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl
    });
  }

  /**
   * Clear all items from cache
   */
  clear(): void {
    this.cache.clear();
  }
  
  /**
   * Delete specific item
   */
  delete(key: string): void {
      this.cache.delete(key);
  }
  
  /**
   * Delete all cache entries whose keys start with the given prefix.
   * e.g. invalidate('inventory_') removes 'inventory_all', 'inventory_123', etc.
   * Use a trailing underscore in the prefix to avoid accidentally matching
   * unrelated keys (e.g. 'inventory_' won't match 'inventoryV2_all').
   */
  invalidate(prefix: string): void {
      for (const key of this.cache.keys()) {
          if (key.startsWith(prefix)) {
              this.cache.delete(key);
          }
      }
  }

  /**
   * Helper to wrap an async call with caching
   */
  async fetch<T>(key: string, fetcher: () => Promise<T>, ttl?: number): Promise<T> {
      const cached = this.get<T>(key);
      if (cached) return cached;

      const data = await fetcher();
      this.set(key, data, ttl);
      return data;
  }
}

export const cache = new CacheManager();
